"use client";

import { Fragment, useEffect, useMemo, useState } from "react";
import AuthenticatedLayout from "../../Layouts/AuthenticatedLayout";
import InfoSection from "../../Components/InfoSection";
import GroupedContainer from "../../Components/GroupedContainer";
import SecondaryButton from "../../Components/SecondaryButton";
import axios from "axios";
import {
  TbSearch,
  TbX,
  TbFilter,
  TbChevronDown,
  TbInfoCircle,
  TbPhoto,
  TbCircleCheck,
  TbAlertCircle,
  TbBuilding,
} from "react-icons/tb";
import { ChevronLeft, ChevronRight } from "lucide-react";

function Snackbar({ message, type = "success", onClose, duration = 5000 }) {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(onClose, 300);
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const bgColor = {
    success: "bg-green-600 border-green-700",
    error: "bg-red-600 border-red-700",
    info: "bg-blue-600 border-blue-700",
    warning: "bg-amber-600 border-amber-700",
  }[type];

  const icon = {
    success: <TbCircleCheck className="w-5 h-5" />,
    error: <TbAlertCircle className="w-5 h-5" />,
    info: <TbInfoCircle className="w-5 h-5" />,
    warning: <TbInfoCircle className="w-5 h-5" />,
  }[type];

  return (
    <div
      className={`fixed bottom-4 right-4 z-50 transform transition-all duration-300 ${
        isVisible ? "translate-x-0 opacity-100" : "translate-x-full opacity-0"
      }`}
    >
      <div
        className={`${bgColor} text-white px-6 py-4 rounded-lg shadow-lg border max-w-sm flex items-start gap-3`}
      >
        <div className="flex-shrink-0 mt-0.5">{icon}</div>
        <div className="flex-1">
          <p className="text-sm font-medium">{message}</p>
        </div>
        <button
          onClick={() => {
            setIsVisible(false);
            setTimeout(onClose, 300);
          }}
          className="flex-shrink-0 text-white/80 hover:text-white ml-2"
        >
          <TbX className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}

const INSPECTION_TYPE_OPTIONS = [
  { value: "all", label: "All Inspection Types" },
  { value: "Pickup", label: "Pickup" },
  { value: "Delivery", label: "Delivery" },
];

const SORT_OPTIONS = [
  { value: "latest_photo_created_at", label: "Order by: Latest Photo" },
  { value: "partner_company_name", label: "Order by: Partner Company" },
  { value: "order_id", label: "Order by: Order ID" },
  { value: "load_transport_id", label: "Order by: Load ID" },
  { value: "load_item_id", label: "Order by: LoadItem ID" },
  { value: "year", label: "Order by: Year" },
  { value: "make", label: "Order by: Make" },
  { value: "model", label: "Order by: Model" },
  { value: "vin", label: "Order by: VIN" },
  { value: "lot_number", label: "Order by: Lot Number" },
  { value: "buyer_number", label: "Order by: Buyer Number" },
  { value: "inspection_photos_count", label: "Order by: Photos Count" },
  { value: "pickup_photos_count", label: "Order by: Pickup Photos Count" },
  { value: "delivery_photos_count", label: "Order by: Delivery Photos Count" },
  { value: "created_at", label: "Order by: LoadItem Created At" },
];

function parsePaginator(apiPayload) {
  if (
    apiPayload?.data &&
    typeof apiPayload.data === "object" &&
    Array.isArray(apiPayload.data.data)
  ) {
    return apiPayload.data;
  }

  return null;
}

export default function InspectionPhotoHierarchy() {
  const API_ORIGIN = useMemo(() => {
    return process.env.REACT_APP_API_ORIGIN || "https://new.cajuu.app";
  }, []);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [rangeFrom, setRangeFrom] = useState(0);
  const [rangeTo, setRangeTo] = useState(0);
  const [perPage, setPerPage] = useState(20);

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");

  const [partnerCompanyFilter, setPartnerCompanyFilter] = useState("");
  const [loadTransportFilter, setLoadTransportFilter] = useState("");
  const [loadItemFilter, setLoadItemFilter] = useState("");
  const [inspectionTypeFilter, setInspectionTypeFilter] = useState("all");
  const [originFilter, setOriginFilter] = useState("");
  const [destinationFilter, setDestinationFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [sortBy, setSortBy] = useState("latest_photo_created_at");
  const [sortDirection, setSortDirection] = useState("desc");

  const [filtersOpen, setFiltersOpen] = useState(false);

  const [kpis, setKpis] = useState({
    totalPartnerCompanies: 0,
    totalLoadTransports: 0,
    totalLoadItems: 0,
    totalInspectionPhotos: 0,
  });

  const [expandedRows, setExpandedRows] = useState({});

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "success",
  });

  const showSnackbar = (message, type = "success") => {
    setSnackbar({ open: true, message, type });
  };

  const closeSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const getToken = () => localStorage.getItem("token");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 450);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const buildServerParams = (page) => {
    const params = {
      page: page || 1,
      per_page: perPage || 20,
      sort_by: sortBy || "latest_photo_created_at",
      sort_direction: sortDirection || "desc",
      with_kpis: 1,
    };

    const q = (debouncedSearchQuery || "").trim();
    if (q) params.q = q;

    const partnerCompany = (partnerCompanyFilter || "").trim();
    if (partnerCompany) {
      params.partner_company_id = partnerCompany;
    }

    const loadTransport = (loadTransportFilter || "").trim();
    if (loadTransport) {
      params.load_transport_id = loadTransport;
    }

    const loadItem = (loadItemFilter || "").trim();
    if (loadItem) {
      params.load_item_id = loadItem;
    }

    if (inspectionTypeFilter && inspectionTypeFilter !== "all") {
      params.inspection_type = inspectionTypeFilter;
    } else {
      params.inspection_type = "all";
    }

    const origin = (originFilter || "").trim();
    if (origin) {
      params.origin = origin;
    }

    const destination = (destinationFilter || "").trim();
    if (destination) {
      params.destination = destination;
    }

    if (dateFrom) params.date_from = `${dateFrom} 00:00:00`;
    if (dateTo) params.date_to = `${dateTo} 23:59:59`;

    return params;
  };

  const resolvePhotoUrl = (path) => {
    const raw = String(path || "").trim();
    if (!raw) return "";

    if (/^https?:\/\//i.test(raw)) {
      return raw;
    }

    const normalized = raw.replace(/^\/+/, "");
    return `${API_ORIGIN}/storage/${normalized}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";

    const d = new Date(String(dateString).replace(" ", "T"));
    if (Number.isNaN(d.getTime())) return "—";

    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "—";

    const d = new Date(String(dateString).replace(" ", "T"));
    if (Number.isNaN(d.getTime())) return "—";

    return d.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const fetchHierarchy = async (page = 1) => {
    setLoading(true);

    try {
      const token = getToken();

      const res = await axios.get(
        `${API_ORIGIN}/api/inspection-photos/hierarchy/paginated`,
        {
          headers: {
            "Screen-Name": "/InspectionPhotoHierarchy",
            Authorization: `Bearer ${token}`,
          },
          params: buildServerParams(page),
        }
      );

      const paginator = parsePaginator(res.data);
      const list = Array.isArray(paginator?.data) ? paginator.data : [];

      const cp = Number(paginator?.current_page ?? page) || 1;
      const lp = Number(paginator?.last_page ?? 1) || 1;
      const tt = Number(paginator?.total ?? list.length) || 0;
      const pp = Number(paginator?.per_page ?? perPage) || perPage;

      setItems(list);
      setCurrentPage(cp);
      setTotalPages(lp);
      setTotalItems(tt);
      setPerPage(pp);

      setRangeFrom(Number(paginator?.from ?? (list.length ? 1 : 0)) || 0);
      setRangeTo(Number(paginator?.to ?? list.length) || 0);

      const serverKpis = paginator?.kpis;
      if (serverKpis && typeof serverKpis === "object") {
        setKpis({
          totalPartnerCompanies: Number(serverKpis.totalPartnerCompanies ?? 0),
          totalLoadTransports: Number(serverKpis.totalLoadTransports ?? 0),
          totalLoadItems: Number(serverKpis.totalLoadItems ?? 0),
          totalInspectionPhotos: Number(serverKpis.totalInspectionPhotos ?? 0),
        });
      } else {
        setKpis({
          totalPartnerCompanies: 0,
          totalLoadTransports: 0,
          totalLoadItems: 0,
          totalInspectionPhotos: 0,
        });
      }
    } catch (e) {
      setItems([]);
      setCurrentPage(1);
      setTotalPages(1);
      setTotalItems(0);
      setRangeFrom(0);
      setRangeTo(0);
      setKpis({
        totalPartnerCompanies: 0,
        totalLoadTransports: 0,
        totalLoadItems: 0,
        totalInspectionPhotos: 0,
      });

      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        "Error loading inspection photo hierarchy.";
      showSnackbar(msg, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHierarchy(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    perPage,
    sortBy,
    sortDirection,
    debouncedSearchQuery,
    partnerCompanyFilter,
    loadTransportFilter,
    loadItemFilter,
    inspectionTypeFilter,
    originFilter,
    destinationFilter,
    dateFrom,
    dateTo,
  ]);

  const clearFilters = () => {
    setSearchQuery("");
    setPartnerCompanyFilter("");
    setLoadTransportFilter("");
    setLoadItemFilter("");
    setInspectionTypeFilter("all");
    setOriginFilter("");
    setDestinationFilter("");
    setDateFrom("");
    setDateTo("");
    setSortBy("latest_photo_created_at");
    setSortDirection("desc");
    setPerPage(20);
    setCurrentPage(1);

    showSnackbar("Filters cleared", "info");
  };

  const toggleExpanded = (rowKey) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowKey]: !prev[rowKey],
    }));
  };

  const goToPage = (pageNumber) => {
    const target = Math.max(1, Math.min(Number(pageNumber) || 1, totalPages || 1));
    if (target === currentPage) return;
    setCurrentPage(target);
  };

  const nextPage = () => {
    if (currentPage >= totalPages) return;
    goToPage(currentPage + 1);
  };

  const prevPage = () => {
    if (currentPage <= 1) return;
    goToPage(currentPage - 1);
  };

  return (
    <AuthenticatedLayout>
      <div className="text-white text-base sm:text-lg md:text-xl lg:text-2xl p-4 sm:p-6 md:p-8">
        <GroupedContainer>
          <InfoSection
            title="INSPECTION PHOTOS"
            description="Global hierarchical listing of inspection photos by Partner Company, Load and Load Item"
          />
        </GroupedContainer>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
          <div className="bg-[#1c1b19] p-4 rounded-xl border border-zinc-600">
            <div className="text-xs text-gray-400 uppercase tracking-wider">
              System Partner Companies
            </div>
            <div className="mt-2 text-lg sm:text-xl font-bold text-white">
              {Number(kpis.totalPartnerCompanies || 0)}
            </div>
            <div className="mt-2 text-[11px] text-gray-500 hidden">
              This KPI is not affected by the applied filters.
            </div>
          </div>

          <div className="bg-[#1c1b19] p-4 rounded-xl border border-zinc-600">
            <div className="text-xs text-gray-400 uppercase tracking-wider">
              Filtered LoadTransports
            </div>
            <div className="mt-2 text-lg sm:text-xl font-bold text-white">
              {Number(kpis.totalLoadTransports || 0)}
            </div>
          </div>

          <div className="bg-[#1c1b19] p-4 rounded-xl border border-zinc-600">
            <div className="text-xs text-gray-400 uppercase tracking-wider">
              Filtered LoadItems
            </div>
            <div className="mt-2 text-lg sm:text-xl font-bold text-white">
              {Number(kpis.totalLoadItems || 0)}
            </div>
          </div>

          <div className="bg-[#1c1b19] p-4 rounded-xl border border-zinc-600">
            <div className="text-xs text-gray-400 uppercase tracking-wider">
              Filtered InspectionPhotos
            </div>
            <div className="mt-2 text-lg sm:text-xl font-bold text-amber-300">
              {Number(kpis.totalInspectionPhotos || 0)}
            </div>
          </div>
        </div>

        <div className="bg-[#1c1b19] p-4 sm:p-6 rounded-xl shadow-sm text-white border border-zinc-600 mt-4 mb-4">
          <div className="flex items-stretch gap-3">
            <button
              type="button"
              onClick={() => setFiltersOpen((prev) => !prev)}
              className="flex-1 flex items-center justify-between gap-3 rounded-lg px-3 py-2 text-left hover:bg-white/5 transition-colors"
            >
              <div className="flex items-center gap-2 min-w-0">
                <TbFilter className="text-amber-500 w-5 h-5 flex-shrink-0" />
                <h2 className="text-lg sm:text-xl font-bold">Filters</h2>
              </div>
              <TbChevronDown
                className={`text-white w-5 h-5 flex-shrink-0 transition-transform duration-200 ${
                  filtersOpen ? "rotate-180" : ""
                }`}
              />
            </button>

            <button
              type="button"
              onClick={clearFilters}
              className="text-sm text-gray-400 hover:text-white flex items-center gap-1 px-2"
            >
              <TbX className="w-4 h-4" />
              Clear Filters
            </button>
          </div>

          {filtersOpen && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
                <div className="relative lg:col-span-2">
                  <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                    <TbSearch className="text-white text-lg" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search company, DOT, order, VIN, make, model, lot, buyer, route or photo..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="w-full pl-10 pr-10 bg-transparent placeholder:text-white py-2 sm:py-3 text-sm border text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => {
                        setSearchQuery("");
                        setCurrentPage(1);
                      }}
                      className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-white"
                    >
                      <TbX className="text-white" />
                    </button>
                  )}
                </div>

                <div className="relative">
                  <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                    <TbBuilding className="text-amber-500 text-lg" />
                  </div>
                  <input
                    type="text"
                    placeholder="Partner Company: name, DOT or ID"
                    value={partnerCompanyFilter}
                    onChange={(e) => {
                      setPartnerCompanyFilter(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="w-full pl-10 pr-10 bg-transparent placeholder:text-white py-2 sm:py-3 text-sm border text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                  {partnerCompanyFilter && (
                    <button
                      type="button"
                      onClick={() => {
                        setPartnerCompanyFilter("");
                        setCurrentPage(1);
                      }}
                      className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-white"
                    >
                      <TbX className="text-white" />
                    </button>
                  )}
                </div>

                <div className="relative">
                  <input
                    type="text"
                    placeholder="LoadTransport: Order ID or ID"
                    value={loadTransportFilter}
                    onChange={(e) => {
                      setLoadTransportFilter(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="w-full px-3 bg-transparent placeholder:text-white py-2 sm:py-3 text-sm border text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                </div>

                <div className="relative">
                  <input
                    type="text"
                    placeholder="LoadItem: Year, Make, Model, VIN or ID"
                    value={loadItemFilter}
                    onChange={(e) => {
                      setLoadItemFilter(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="w-full px-3 bg-transparent placeholder:text-white py-2 sm:py-3 text-sm border text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                </div>

                <div className="relative">
                  <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                    <TbFilter className="text-amber-500 text-lg" />
                  </div>
                  <select
                    value={inspectionTypeFilter}
                    onChange={(e) => {
                      setInspectionTypeFilter(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="w-full pl-10 pr-12 bg-transparent text-white py-2 sm:py-3 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all appearance-none"
                  >
                    {INSPECTION_TYPE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value} className="bg-[#1c1b19]">
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                    <TbChevronDown className="text-white text-lg" />
                  </div>
                </div>

                <div className="relative">
                  <input
                    type="text"
                    placeholder="Origin: pickup address, city, state or ZIP"
                    value={originFilter}
                    onChange={(e) => {
                      setOriginFilter(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="w-full px-3 bg-transparent placeholder:text-white py-2 sm:py-3 text-sm border text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                </div>

                <div className="relative">
                  <input
                    type="text"
                    placeholder="Destination: delivery address, city, state or ZIP"
                    value={destinationFilter}
                    onChange={(e) => {
                      setDestinationFilter(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="w-full px-3 bg-transparent placeholder:text-white py-2 sm:py-3 text-sm border text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                </div>

                <div className="md:col-span-2 lg:col-span-1">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 lg:grid-cols-1 xl:grid-cols-2">
                    <div className="relative">
                      <span className="absolute -top-2 left-3 bg-[#1c1b19] px-1 text-xs text-gray-400">
                        From
                      </span>
                      <input
                        type="date"
                        value={dateFrom}
                        onChange={(e) => {
                          setDateFrom(e.target.value);
                          setCurrentPage(1);
                        }}
                        onFocus={(e) => e.target.showPicker?.()}
                        style={{ colorScheme: "dark" }}
                        className="w-full bg-transparent text-white py-2 sm:py-3 text-sm border rounded-lg px-3 focus:outline-none focus:ring-2 focus:ring-amber-500"
                      />
                    </div>

                    <div className="relative">
                      <span className="absolute -top-2 left-3 bg-[#1c1b19] px-1 text-xs text-gray-400">
                        To
                      </span>
                      <input
                        type="date"
                        value={dateTo}
                        onChange={(e) => {
                          setDateTo(e.target.value);
                          setCurrentPage(1);
                        }}
                        onFocus={(e) => e.target.showPicker?.()}
                        style={{ colorScheme: "dark" }}
                        className="w-full bg-transparent text-white py-2 sm:py-3 text-sm border rounded-lg px-3 focus:outline-none focus:ring-2 focus:ring-amber-500"
                      />
                    </div>
                  </div>
                </div>

                <div className="relative">
                  <select
                    value={String(perPage)}
                    onChange={(e) => {
                      setPerPage(parseInt(e.target.value, 10));
                      setCurrentPage(1);
                    }}
                    className="w-full px-3 pr-12 bg-transparent text-white py-2 sm:py-3 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all appearance-none"
                  >
                    <option value="10" className="bg-[#1c1b19]">
                      10 / page
                    </option>
                    <option value="20" className="bg-[#1c1b19]">
                      20 / page
                    </option>
                    <option value="50" className="bg-[#1c1b19]">
                      50 / page
                    </option>
                    <option value="100" className="bg-[#1c1b19]">
                      100 / page
                    </option>
                  </select>
                  <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                    <TbChevronDown className="text-white text-lg" />
                  </div>
                </div>

                <div className="relative">
                  <select
                    value={sortBy}
                    onChange={(e) => {
                      setSortBy(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="w-full px-3 pr-12 bg-transparent text-white py-2 sm:py-3 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all appearance-none"
                  >
                    {SORT_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value} className="bg-[#1c1b19]">
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                    <TbChevronDown className="text-white text-lg" />
                  </div>
                </div>

                <div className="relative">
                  <select
                    value={sortDirection}
                    onChange={(e) => {
                      setSortDirection(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="w-full px-3 pr-12 bg-transparent text-white py-2 sm:py-3 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all appearance-none"
                  >
                    <option value="asc" className="bg-[#1c1b19]">
                      Direction: ASC
                    </option>
                    <option value="desc" className="bg-[#1c1b19]">
                      Direction: DESC
                    </option>
                  </select>
                  <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                    <TbChevronDown className="text-white text-lg" />
                  </div>
                </div>
              </div>

              <div className="mt-3 text-xs text-white/70">
                Server pagination: page {currentPage} of {totalPages} • total {totalItems} • per page {perPage}
              </div>
            </>
          )}
        </div>

        <div className="mt-6 overflow-x-auto rounded-xl border border-zinc-600">
          <table className="min-w-full divide-y divide-zinc-600">
            <thead className="bg-[#1c1b19]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Partner Company
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Load
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Load Item / Vehicle
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Visible Photos
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Pickup
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Delivery
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Latest Photo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody className="bg-[#1c1b19]/50 divide-y divide-zinc-600">
              {loading ? (
                <tr>
                  <td colSpan="8" className="px-6 py-8 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
                    </div>
                    <p className="mt-2 text-gray-400">Loading inspection photo hierarchy...</p>
                  </td>
                </tr>
              ) : items.length > 0 ? (
                items.map((row) => {
                  const rowKey = String(row?.load_item?.id || "");
                  const isExpanded = !!expandedRows[rowKey];
                  const visiblePhotos = Array.isArray(row?.inspection_photos)
                    ? row.inspection_photos
                    : [];

                  return (
                    <Fragment key={rowKey}>
                      <tr className="hover:bg-zinc-800/30 transition-colors">
                        <td className="px-6 py-4 align-top text-sm text-gray-300">
                          <div className="font-semibold text-white">
                            {row?.partner_company?.name || "—"}
                          </div>
                          <div className="text-xs text-white/60 mt-1">
                            ID: #{row?.partner_company?.id || "—"}
                          </div>
                          {row?.partner_company?.dot ? (
                            <div className="text-xs text-white/60">
                              DOT: {row.partner_company.dot}
                            </div>
                          ) : null}
                          {row?.partner_company?.email ? (
                            <div className="text-xs text-white/60 break-all">
                              {row.partner_company.email}
                            </div>
                          ) : null}
                        </td>

                        <td className="px-6 py-4 align-top text-sm text-gray-300">
                          <div className="font-semibold text-white">
                            {row?.load_transport?.order_id || "—"}
                          </div>
                          <div className="text-xs text-white/60 mt-1">
                            Load #{row?.load_transport?.id || "—"}
                          </div>
                          <div className="text-xs text-white/60 mt-1">
                            {row?.load_transport?.pickup_city || "—"},{" "}
                            {row?.load_transport?.pickup_state || "—"} →{" "}
                            {row?.load_transport?.delivery_city || "—"},{" "}
                            {row?.load_transport?.delivery_state || "—"}
                          </div>
                        </td>

                        <td className="px-6 py-4 align-top text-sm text-gray-300">
                          <div className="font-semibold text-white">
                            {row?.load_item?.year || "—"} {row?.load_item?.make || ""}{" "}
                            {row?.load_item?.model || ""}
                          </div>
                          <div className="text-xs text-white/60 mt-1">
                            LoadItem #{row?.load_item?.id || "—"}
                          </div>
                          <div className="text-xs text-white/60">
                            VIN: {row?.load_item?.vin || "—"}
                          </div>
                          <div className="text-xs text-white/60">
                            Lot: {row?.load_item?.lot_number || "—"} • Buyer:{" "}
                            {row?.load_item?.buyer_number || "—"}
                          </div>
                        </td>

                        <td className="px-6 py-4 align-top text-sm font-medium text-white">
                          {Number(row?.counts?.inspection_photos || 0)}
                        </td>

                        <td className="px-6 py-4 align-top text-sm font-medium text-amber-300">
                          {Number(row?.counts?.pickup_photos || 0)}
                        </td>

                        <td className="px-6 py-4 align-top text-sm font-medium text-emerald-300">
                          {Number(row?.counts?.delivery_photos || 0)}
                        </td>

                        <td className="px-6 py-4 align-top text-sm text-gray-300">
                          {formatDateTime(row?.latest_photo_created_at)}
                        </td>

                        <td className="px-6 py-4 align-top whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => toggleExpanded(rowKey)}
                            className="px-3 py-2 text-xs rounded-lg border border-amber-700 text-amber-200 hover:bg-amber-700/15 transition-colors flex items-center gap-2"
                          >
                            <TbPhoto className="w-4 h-4" />
                            {isExpanded ? "Hide Photos" : `View Photos (${visiblePhotos.length})`}
                          </button>
                        </td>
                      </tr>

                      {isExpanded && (
                        <tr className="bg-black/20">
                          <td colSpan="8" className="px-6 py-5">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div className="border border-zinc-700 rounded-xl p-4 bg-zinc-900/20">
                                <div className="text-xs text-gray-400 uppercase tracking-wider">
                                  Partner Company
                                </div>
                                <div className="mt-2 text-sm font-semibold text-white">
                                  {row?.partner_company?.name || "—"}
                                </div>
                                <div className="mt-1 text-xs text-white/60">
                                  ID: #{row?.partner_company?.id || "—"}
                                </div>
                                <div className="mt-1 text-xs text-white/60">
                                  DOT: {row?.partner_company?.dot || "—"}
                                </div>
                                <div className="mt-1 text-xs text-white/60 break-all">
                                  Email: {row?.partner_company?.email || "—"}
                                </div>
                                <div className="mt-1 text-xs text-white/60">
                                  Phone: {row?.partner_company?.phone || "—"}
                                </div>
                              </div>

                              <div className="border border-zinc-700 rounded-xl p-4 bg-zinc-900/20">
                                <div className="text-xs text-gray-400 uppercase tracking-wider">
                                  LoadTransport
                                </div>
                                <div className="mt-2 text-sm font-semibold text-white">
                                  {row?.load_transport?.order_id || "—"}
                                </div>
                                <div className="mt-1 text-xs text-white/60">
                                  Load ID: #{row?.load_transport?.id || "—"}
                                </div>
                                <div className="mt-1 text-xs text-white/60">
                                  Pickup: {row?.load_transport?.pickup_address || "—"}
                                  {row?.load_transport?.pickup_city || row?.load_transport?.pickup_state
                                    ? `, ${row?.load_transport?.pickup_city || "—"}, ${row?.load_transport?.pickup_state || "—"}`
                                    : ""}
                                  {row?.load_transport?.pickup_zipcode
                                    ? ` ${row.load_transport.pickup_zipcode}`
                                    : ""}
                                </div>
                                <div className="mt-1 text-xs text-white/60">
                                  Delivery: {row?.load_transport?.delivery_address || "—"}
                                  {row?.load_transport?.delivery_city || row?.load_transport?.delivery_state
                                    ? `, ${row?.load_transport?.delivery_city || "—"}, ${row?.load_transport?.delivery_state || "—"}`
                                    : ""}
                                  {row?.load_transport?.delivery_zipcode
                                    ? ` ${row.load_transport.delivery_zipcode}`
                                    : ""}
                                </div>
                              </div>

                              <div className="border border-zinc-700 rounded-xl p-4 bg-zinc-900/20">
                                <div className="text-xs text-gray-400 uppercase tracking-wider">
                                  LoadItem
                                </div>
                                <div className="mt-2 text-sm font-semibold text-white">
                                  {row?.load_item?.year || "—"} {row?.load_item?.make || ""}{" "}
                                  {row?.load_item?.model || ""}
                                </div>
                                <div className="mt-1 text-xs text-white/60">
                                  LoadItem ID: #{row?.load_item?.id || "—"}
                                </div>
                                <div className="mt-1 text-xs text-white/60">
                                  VIN: {row?.load_item?.vin || "—"}
                                </div>
                                <div className="mt-1 text-xs text-white/60">
                                  Lot: {row?.load_item?.lot_number || "—"} • Buyer:{" "}
                                  {row?.load_item?.buyer_number || "—"}
                                </div>
                                <div className="mt-1 text-xs text-white/60">
                                  Created: {formatDate(row?.load_item?.created_at)}
                                </div>
                              </div>
                            </div>

                            <div className="mt-5">
                              <div className="text-sm font-semibold text-white mb-3">
                                Visible inspection photos
                              </div>

                              {visiblePhotos.length > 0 ? (
                                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                                  {visiblePhotos.map((photo) => {
                                    const photoUrl = resolvePhotoUrl(photo?.path);

                                    return (
                                      <div
                                        key={photo.id}
                                        className="border border-zinc-700 rounded-xl p-3 bg-zinc-900/20"
                                      >
                                        <a
                                          href={photoUrl || "#"}
                                          target="_blank"
                                          rel="noreferrer"
                                          className="block"
                                        >
                                          <div className="aspect-video bg-black/30 rounded-lg overflow-hidden border border-zinc-700 flex items-center justify-center">
                                            {photoUrl ? (
                                              <img
                                                src={photoUrl}
                                                alt={`Inspection photo ${photo.id}`}
                                                className="w-full h-full object-cover"
                                                onError={(e) => {
                                                  e.currentTarget.style.display = "none";
                                                }}
                                              />
                                            ) : (
                                              <div className="text-xs text-white/50 px-4 text-center">
                                                No preview available
                                              </div>
                                            )}
                                          </div>
                                        </a>

                                        <div className="mt-3 text-xs text-white/80 space-y-1">
                                          <div>
                                            <span className="text-white/50">Photo ID:</span> #{photo.id}
                                          </div>
                                          <div>
                                            <span className="text-white/50">Inspection Type:</span>{" "}
                                            {photo.inspection_type || "—"}
                                          </div>
                                          <div>
                                            <span className="text-white/50">Origin:</span>{" "}
                                            {photo.origin || "—"}
                                          </div>
                                          <div>
                                            <span className="text-white/50">Created:</span>{" "}
                                            {formatDateTime(photo.created_at)}
                                          </div>
                                          <div className="break-all">
                                            <span className="text-white/50">Path:</span>{" "}
                                            {photo.path || "—"}
                                          </div>
                                          {photo.description ? (
                                            <div className="text-white/90">
                                              <span className="text-white/50">Description:</span>{" "}
                                              {photo.description}
                                            </div>
                                          ) : null}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : (
                                <div className="text-sm text-white/60 border border-zinc-700 rounded-xl p-4 bg-zinc-900/20">
                                  No inspection photos matched the current filters for this LoadItem.
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="8" className="px-6 py-8 text-center text-gray-400">
                    <div className="text-gray-400">
                      <TbInfoCircle className="w-10 h-10 mx-auto mb-2 opacity-50" />
                      No inspection photo hierarchy rows found matching your filters.
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <>
            <div className="flex items-center justify-center space-x-2 mt-6">
              <SecondaryButton
                variant="outline"
                size="sm"
                onClick={prevPage}
                disabled={currentPage === 1 || loading}
                className="text-white border-white hover:bg-white/10"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </SecondaryButton>

              <div className="flex items-center space-x-1">
                {(() => {
                  let startPage = Math.max(1, currentPage - 2);
                  const endPage = Math.min(totalPages, startPage + 4);

                  if (endPage - startPage < 4 && startPage > 1) {
                    startPage = Math.max(1, endPage - 4);
                  }

                  const pageNumbers = [];
                  for (let i = startPage; i <= endPage; i++) {
                    pageNumbers.push(
                      <SecondaryButton
                        key={i}
                        size="sm"
                        onClick={() => goToPage(i)}
                        disabled={loading}
                        className={
                          currentPage === i
                            ? "bg-amber-600 text-black hover:bg-white/90"
                            : "text-black border-white hover:bg-white/10"
                        }
                      >
                        {i}
                      </SecondaryButton>
                    );
                  }
                  return pageNumbers;
                })()}
              </div>

              <SecondaryButton
                variant="outline"
                size="sm"
                onClick={nextPage}
                disabled={currentPage === totalPages || loading}
                className="text-white border-white hover:bg-white/10"
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </SecondaryButton>
            </div>

            <div className="text-center text-sm text-white/70 mt-2">
              Showing {rangeFrom}-{rangeTo} of {totalItems} hierarchical rows
            </div>
          </>
        )}
      </div>

      {snackbar.open && (
        <Snackbar
          message={snackbar.message}
          type={snackbar.type}
          onClose={closeSnackbar}
          duration={5000}
        />
      )}
    </AuthenticatedLayout>
  );
}